import { useState, useEffect, useCallback } from 'react'
import { createLogger } from '../utils/logger'

const logger = createLogger('PushSubscription')

/**
 * usePushSubscription - manages browser web push subscription lifecycle
 * @returns {{ isSupported, isSubscribed, isLoading, subscribe, unsubscribe }}
 */
export function usePushSubscription() {
  const [isSupported, setIsSupported] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [subscription, setSubscription] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // Detect support and check existing subscription on mount
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window
    setIsSupported(supported)

    if (supported) {
      // Use getRegistration() — unlike .ready, it doesn't hang when no SW is registered
      navigator.serviceWorker.getRegistration()
        .then((reg) => {
          if (!reg) return null
          return reg.pushManager.getSubscription()
        })
        .then((sub) => {
          if (sub) {
            setSubscription(sub)
            setIsSubscribed(true)
          }
        })
        .catch((err) => logger.warn('Could not check existing push subscription', err))
    }
  }, [])

  const subscribe = useCallback(async () => {
    if (!isSupported || isLoading) return
    setIsLoading(true)
    try {
      const keyRes = await fetch('/api/push/vapid-public-key')
      if (keyRes.status === 503) throw Object.assign(new Error('Push not configured on server'), { code: 'PUSH_NOT_CONFIGURED' })
      if (!keyRes.ok) throw new Error('Push not configured on server')
      const { publicKey } = await keyRes.json()

      // getRegistration() returns undefined immediately if no SW exists (unlike
      // .ready which hangs forever). If the SW is still installing on first visit,
      // fall back to .ready with a timeout — it resolves once the SW is active.
      const reg = await navigator.serviceWorker.getRegistration('/')
      if (!reg) throw new Error('Service worker not registered')
      const registration = reg.active
        ? reg
        : await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Service worker not ready')), 10000)
            ),
          ])

      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })

      const saveRes = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sub.toJSON()),
      })
      if (!saveRes.ok) throw new Error('Failed to save subscription')

      setSubscription(sub)
      setIsSubscribed(true)
      logger.info('Push subscription created')
    } catch (err) {
      if (err?.code !== 'PUSH_NOT_CONFIGURED') {
        logger.error('Failed to subscribe to push notifications', err)
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [isSupported, isLoading])

  const unsubscribe = useCallback(async () => {
    if (!subscription || isLoading) return
    setIsLoading(true)
    try {
      await fetch('/api/push/subscribe', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      })
      await subscription.unsubscribe()
      setSubscription(null)
      setIsSubscribed(false)
      logger.info('Push subscription removed')
    } catch (err) {
      logger.error('Failed to unsubscribe from push notifications', err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [subscription, isLoading])

  return { isSupported, isSubscribed, isLoading, subscribe, unsubscribe }
}

// Converts a base64url VAPID public key to a Uint8Array for the browser API
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}
